import Drawer from '@mui/material/Drawer';
import { useEffect, useState } from 'react';
import { CSSObject, styled, Theme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocation } from 'react-router-dom';
import SidebarHeader from './component/SidebarHeader';
import UserMenu from './component/UserMenu';
import TabMenu from './component/TabMenu';
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from '../../utils/Constants';
import { fromLayout } from '../../store';
import routes from '../routes';
import { RouteConfig } from '../../routes';

const openedMixin = (theme: Theme): CSSObject => ({
  width: SIDEBAR_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: SIDEBAR_COLLAPSED_WIDTH,
  [theme.breakpoints.up('sm')]: {
    width: SIDEBAR_COLLAPSED_WIDTH,
  },
});

const StyledDrawer = styled(Drawer, { shouldForwardProp: prop => prop !== 'open' })(
  ({ theme }) => ({
    width: SIDEBAR_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    '& .MuiDrawer-paper': {
      backgroundColor: '#f7f9f8',
      borderRight: '1px solid #eef2f0',
      padding: '16px 0',
    },
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': openedMixin(theme),
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme),
        },
      },
    ],
  }),
);

export const Sidebar = (
  { filteredRoutes }: {
    filteredRoutes: RouteConfig[];
  },
) => {
  const matches = useMediaQuery('(max-width:1280px)');
  const { pathname } = useLocation();
  const currentRoute = routes.find(route => pathname === route.path);
  // Collapsed by default; once the user expands it, it stays expanded across menu pages.
  const [open, setOpen] = useState(false);
  const showSidebar = useSelector(fromLayout.selectShowSidebar);
  const handleSidebarToggle = () => {
    setOpen(!open);
  };

  // Pages outside the menu (e.g. checkout) and narrow screens still fold it away.
  useEffect(() => {
    if (!currentRoute?.showInMenu || matches) {
      setOpen(false);
    }
  }, [matches, currentRoute]);

  return (
    <StyledDrawer
      variant="permanent"
      open={open}
      sx={theme => ({
        display: showSidebar ? 'flex' : 'none',
        [theme.breakpoints.down('tablet')]: {
          display: 'none',
        },
      })}
    >
      {/* Sidebar Header */}
      <SidebarHeader
        open={open}
        collapsible
        sidebarToggle={handleSidebarToggle}
      />
      {/* User Menu */}
      <UserMenu
        open={open}
        expandSidebar={() => setOpen(true)}
      />
      {/* Tab Menu */}
      <TabMenu
        open={open}
        filteredRoutes={filteredRoutes}
      />
    </StyledDrawer>
  );
};
